import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { conversationPath } from '../DutyAssistant/statusColors'
import ServiceTransactions from '../../components/ServiceTransactions'
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Table,
  Tag,
  Timeline,
  Typography
} from 'antd'
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons'
import {
  errorNotification,
  successNotification,
  vehicleHistoryOrderActionsUrl,
  vehicleHistoryOrderUrl,
  vehicleHistoryProductsUrl
} from '../../config'
import { getQuiet, postQuiet } from './api'
import ContactButtons from '../Tracking/ContactButtons'
import {
  CloseModal,
  NoteModal,
  RefundModal,
  RetryPartButton,
  SwitchProductModal
} from './OrderActions'
import DeliveriesCard from './DeliveriesCard'
import { ChannelTag, PartStatusTag, StatusTag } from './tags'
import {
  COST_SOURCE_LABELS,
  IDENTIFIER_LABELS,
  MARKET_LABELS,
  actionLabel,
  alertLabel,
  duration,
  formatDateTime,
  money,
  serverMessage,
  usd
} from './utils'

// One order, in full. The page a Telegram deep link opens cold on somebody's phone, so it resolves
// on its own and says everything without a second click.
//
// This is the only screen in the product that shows the supplier's own report ids. The customer
// API hides them because GlobalVIN serves a report to anyone holding one; our team needs them to
// chase a stuck order, and the file links here are signed and expiring for exactly that reason —
// the link is the thing that gets forwarded.
const OrderDetail = ({ match, history }) => {
  const orderId = match.params.id
  const [order, setOrder] = useState(null)
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [modal, setModal] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const response = await getQuiet(vehicleHistoryOrderUrl(orderId))
    setLoading(false)
    if (response.success) setOrder(response.data)
    else errorNotification(serverMessage(response, 'Could not open that order.'))
  }, [orderId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    getQuiet(vehicleHistoryProductsUrl).then(response => {
      if (response.success) setProducts((response.data || {}).results || [])
    })
  }, [])

  const act = async payload => {
    setSaving(true)
    const response = await postQuiet(vehicleHistoryOrderActionsUrl(orderId), payload)
    setSaving(false)
    if (!response.success) {
      errorNotification(serverMessage(response, 'That did not work.'))
      return
    }
    setOrder(response.data)
    setModal(null)
    successNotification('Done.')
  }

  if (!order) {
    return (
      <Card loading={loading} title="Order">
        {loading ? null : 'Not found.'}
      </Card>
    )
  }

  const car = ((order.checkResult || {}).car) || {}
  const openAlerts = (order.alerts || []).filter(alert => !alert.clearedAt)

  /* eslint-disable react/display-name */
  const partColumns = [
    { title: 'Report', dataIndex: 'label', key: 'label' },
    {
      title: 'Status',
      key: 'status',
      render: (_, part) => (
        <Space direction="vertical" size={0}>
          <PartStatusTag status={part.status} />
          {part.supplierStatus ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              supplier: {part.supplierStatus}
            </Typography.Text>
          ) : null}
        </Space>
      )
    },
    {
      title: 'Supplier id',
      key: 'supplier',
      render: (_, part) => (
        <Space direction="vertical" size={0}>
          {part.supplierReportId ? (
            <Typography.Text copyable style={{ fontSize: 12 }}>
              {part.supplierReportId}
            </Typography.Text>
          ) : (
            '—'
          )}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {part.attempts} attempt{part.attempts === 1 ? '' : 's'} · {part.polls} polls
          </Typography.Text>
        </Space>
      )
    },
    {
      title: 'Took',
      key: 'took',
      render: (_, part) => duration(part.requestedAt, part.completedAt)
    },
    {
      title: 'Cost',
      key: 'cost',
      render: (_, part) => (
        <Space direction="vertical" size={0}>
          <span>{part.costUsd === null ? '—' : usd(part.costUsd)}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {COST_SOURCE_LABELS[part.costSource] || part.costSource || '—'}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: 'Supplier refunded',
      key: 'refund',
      render: (_, part) =>
        part.supplierRefundUsd ? (
          usd(part.supplierRefundUsd)
        ) : part.creditsRefunded === true ? (
          <Tag color="green">Yes</Tag>
        ) : part.creditsRefunded === false ? (
          <Tag>No</Tag>
        ) : (
          // Null is "the supplier did not say", which for the USA and Global families is the
          // honest answer and a different fact from "it was not refunded".
          <Typography.Text type="secondary">Not said</Typography.Text>
        )
    },
    {
      title: 'Problem',
      key: 'error',
      render: (_, part) =>
        part.errorMessage ? (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            {part.errorMessage}
          </Typography.Text>
        ) : (
          '—'
        )
    },
    {
      title: '',
      key: 'actions',
      render: (_, part) => (
        <Space>
          {part.fileUrl ? (
            <Button size="small" type="link" href={part.fileUrl} target="_blank" rel="noreferrer">
              Open file
            </Button>
          ) : null}
          <RetryPartButton
            part={part}
            saving={saving}
            onRetry={row => act({ kind: 'retry_part', partId: row.id })}
          />
        </Space>
      )
    }
  ]
  /* eslint-enable react/display-name */

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/vehicle-history/orders')}>
          All orders
        </Button>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
          Refresh
        </Button>
      </Space>

      {openAlerts.length ? (
        <Alert
          type={openAlerts.some(alert => alert.priority === 'urgent') ? 'error' : 'warning'}
          showIcon
          style={{ marginBottom: 16 }}
          message="Needs attention"
          description={
            <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
              {openAlerts.map(alert => (
                <li key={alert.id}>
                  <strong>{alertLabel(alert.kind)}</strong> — {alert.detail}
                </li>
              ))}
            </ul>
          }
        />
      ) : null}

      <Row gutter={16}>
        <Col xs={24} lg={14}>
          <Card title="The order" style={{ marginBottom: 16 }}>
            <Descriptions size="small" bordered column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Number">
                <Typography.Text copyable strong>
                  {order.identifier}
                </Typography.Text>
                <div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {IDENTIFIER_LABELS[order.identifierKind]}
                    {order.identifierChanges
                      ? ` · changed ${order.identifierChanges} time${
                        order.identifierChanges === 1 ? '' : 's'
                      }`
                      : ''}
                  </Typography.Text>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <StatusTag status={order.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Package">
                {order.productName}
                <div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {MARKET_LABELS[order.market] || order.market}
                  </Typography.Text>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Car">
                {car.make ? `${car.make} ${car.model || ''} ${car.year || ''}`.trim() : '—'}
                {car.manufacturer && !car.make ? car.manufacturer : null}
              </Descriptions.Item>
              <Descriptions.Item label="Customer">
                <ContactButtons
                  phone={order.contactPhone}
                  message={`Hello, about your vehicle history report for ${order.identifier}.`}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Came from">
                <Space size={8} wrap>
                  <ChannelTag channel={order.channel} />
                  {order.conversationId ? (
                    <Link
                      to={conversationPath('vehicle_history', order.conversationId)}
                    >
                      Open the chat
                    </Link>
                  ) : null}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {formatDateTime(order.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Submitted">
                {formatDateTime(order.submittedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Finished" span={2}>
                {formatDateTime(order.completedAt)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Reports" style={{ marginBottom: 16 }}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={order.parts || []}
              columns={partColumns}
              scroll={{ x: 'max-content' }}
            />
            {(order.parts || []).length ? null : (
              <Typography.Text type="secondary">
                Nothing has been ordered yet — the customer has not confirmed the number.
              </Typography.Text>
            )}
          </Card>
          <DeliveriesCard order={order} saving={saving} onAct={act} />
        </Col>

        <Col xs={24} lg={10}>
          {/* What our team can actually DO about the money, above the read-only summary: a
              customer who sent it to our merchant number is waiting on somebody here
              (owner, 2026-10-30). */}
          <ServiceTransactions
            service="vehicle_history"
            reference={((order || {}).payment || {}).reference}
            onChanged={load}
          />

          <Card title="Payment" style={{ marginBottom: 16 }}>
            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label="Charged">
                {money(order.priceAmount, order.priceCurrency)}
              </Descriptions.Item>
              <Descriptions.Item label="Paid">
                {order.paid ? formatDateTime((order.payment || {}).paidAt) : 'Not paid'}
              </Descriptions.Item>
              <Descriptions.Item label="How it arrived">
                {(order.payment || {}).mode || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Reference">
                {(order.payment || {}).reference ? (
                  <Typography.Text copyable style={{ fontSize: 12 }}>
                    {order.payment.reference}
                  </Typography.Text>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Cost to us">
                {order.supplierCostUsd === null ? '—' : usd(order.supplierCostUsd)}
              </Descriptions.Item>
              <Descriptions.Item label="Refunded so far">
                {money(order.refundedTotal, order.priceCurrency)}
              </Descriptions.Item>
            </Descriptions>

            {order.suggestedRefund ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message={`A refund of ${money(
                  order.suggestedRefund.amount,
                  order.suggestedRefund.currency
                )} is owed`}
                description="Refund it in Paystack, then record it here with its reference."
              />
            ) : null}
          </Card>

          <Card title="What our team can do" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button block onClick={() => setModal('refund')} disabled={!order.paid}>
                Record a refund
              </Button>
              <Button
                block
                onClick={() => setModal('switch')}
                disabled={!['no_data', 'failed', 'partial'].includes(order.status)}
              >
                Switch to another report
              </Button>
              <Button block onClick={() => setModal('note')}>
                Add a note
              </Button>
              <Button
                block
                danger
                onClick={() => setModal('close')}
                disabled={order.status === 'closed'}
              >
                Close this order
              </Button>
            </Space>
          </Card>

          <Card title="What happened">
            <Timeline>
              {(order.timeline || []).map(entry => (
                <Timeline.Item key={entry.id}>
                  <div>
                    <strong>{actionLabel(entry.kind)}</strong>{' '}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDateTime(entry.createdAt)} · {entry.actor}
                    </Typography.Text>
                  </div>
                  {entry.note ? <div>{entry.note}</div> : null}
                  {entry.oldIdentifier ? (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {entry.oldIdentifier} → {entry.newIdentifier}
                      </Typography.Text>
                    </div>
                  ) : null}
                  {entry.amount ? (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {money(entry.amount, entry.currency)} · {entry.paystackReference}
                      </Typography.Text>
                    </div>
                  ) : null}
                </Timeline.Item>
              ))}
            </Timeline>
            {(order.timeline || []).length ? null : (
              <Typography.Text type="secondary">Nothing yet.</Typography.Text>
            )}
          </Card>
        </Col>
      </Row>

      <RefundModal
        open={modal === 'refund'}
        saving={saving}
        order={order}
        onCancel={() => setModal(null)}
        onSubmit={act}
      />
      <SwitchProductModal
        open={modal === 'switch'}
        saving={saving}
        order={order}
        products={products}
        onCancel={() => setModal(null)}
        onSubmit={act}
      />
      <NoteModal
        open={modal === 'note'}
        saving={saving}
        onCancel={() => setModal(null)}
        onSubmit={act}
      />
      <CloseModal
        open={modal === 'close'}
        saving={saving}
        onCancel={() => setModal(null)}
        onSubmit={act}
      />
    </div>
  )
}

OrderDetail.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
}

export default OrderDetail
